// src/pages/finance-portal/tax-exemptions/TaxExemptions.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    IconButton, Tooltip, Autocomplete,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../../utils/axiosInstance';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    EXPIRED: { bg: '#fecaca', color: '#991b1b' },
    PENDING: { bg: '#fef3c7', color: '#92400e' },
    REVOKED: { bg: '#f1f5f9', color: '#475569' },
};

const EXEMPTION_TYPES = [
    { value: '', label: 'All Types' },
    { value: 'RESALE', label: 'Resale' },
    { value: 'GOVERNMENT', label: 'Government' },
    { value: 'NONPROFIT', label: 'Nonprofit' },
    { value: 'MANUFACTURING', label: 'Manufacturing' },
    { value: 'AGRICULTURAL', label: 'Agricultural' },
    { value: 'MEDICAL', label: 'Medical' },
];

const STATUSES = [
    { value: '', label: 'All Statuses' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'EXPIRED', label: 'Expired' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'REVOKED', label: 'Revoked' },
];

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC',
];

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

export default function TaxExemptions() {
    const { enqueueSnackbar } = useSnackbar();
    const [exemptions, setExemptions] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCustomer, setFilterCustomer] = useState('');

    // Create Dialog
    const [formOpen, setFormOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        certificate_number: '', exemption_type: 'RESALE', customer_id: '',
        state_code: '', issue_date: '', expiration_date: '', document_url: '',
    });

    // Customer autocomplete
    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Check Dialog
    const [checkOpen, setCheckOpen] = useState(false);
    const [checkCustomerId, setCheckCustomerId] = useState('');
    const [checkStateCode, setCheckStateCode] = useState('');
    const [checkResult, setCheckResult] = useState(null);
    const [checking, setChecking] = useState(false);

    const fetchExemptions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (filterType) params.type = filterType;
            if (filterStatus) params.status = filterStatus;
            if (filterCustomer) params.customer = filterCustomer;
            const res = await axiosInstance.get('/api/finance/tax/exemptions', { params });
            if (res.data.success) {
                setExemptions(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load exemptions');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, filterType, filterStatus, filterCustomer]);

    useEffect(() => { fetchExemptions(); }, [fetchExemptions]);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const res = await axiosInstance.get('/api/finance/customers', { params: { per_page: 50, search: customerSearch } });
                if (res.data.success) setCustomers(res.data.data || []);
            } catch { /* silent */ }
        };
        if (customerSearch.length >= 1) fetchCustomers();
    }, [customerSearch]);

    const openCreateForm = () => {
        setFormData({
            certificate_number: '', exemption_type: 'RESALE', customer_id: '',
            state_code: '', issue_date: '', expiration_date: '', document_url: '',
        });
        setSelectedCustomer(null);
        setFormOpen(true);
    };

    const handleCreate = async () => {
        setSaving(true);
        try {
            const payload = { ...formData };
            if (selectedCustomer) payload.customer_id = selectedCustomer.id;
            await axiosInstance.post('/api/finance/tax/exemptions', payload);
            enqueueSnackbar('Exemption created', { variant: 'success' });
            setFormOpen(false);
            fetchExemptions();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to create exemption', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleVerify = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/tax/exemptions/${id}/verify`);
            enqueueSnackbar('Exemption verified', { variant: 'success' });
            fetchExemptions();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to verify exemption', { variant: 'error' });
        }
    };

    const handleCheck = async () => {
        setChecking(true);
        setCheckResult(null);
        try {
            const res = await axiosInstance.get('/api/finance/tax/exemptions/check', {
                params: { customer_id: checkCustomerId, state_code: checkStateCode },
            });
            if (res.data.success) {
                setCheckResult(res.data.data);
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Check failed', { variant: 'error' });
        } finally {
            setChecking(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <GavelIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Tax Exemptions</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Manage customer tax exemption certificates</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            startIcon={<SearchIcon />}
                            onClick={() => setCheckOpen(true)}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Check Exemption
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openCreateForm}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            Add Exemption
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small" select label="Type" value={filterType}
                        onChange={e => { setFilterType(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 160 }}
                    >
                        {EXEMPTION_TYPES.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small" select label="Status" value={filterStatus}
                        onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 140 }}
                    >
                        {STATUSES.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontFamily }}>{s.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small" label="Customer Filter" value={filterCustomer}
                        onChange={e => { setFilterCustomer(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 180 }}
                        placeholder="Customer name or ID"
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Certificate #', 'Type', 'Customer', 'State', 'Issue Date', 'Expiration', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                    ))
                                ) : exemptions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No exemptions found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : exemptions.map(ex => (
                                    <TableRow key={ex.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{ex.certificate_number || '\u2014'}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip label={getStatusLabel(ex.exemption_type)} size="small"
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: '#dbeafe', color: '#1e40af' }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{ex.customer_name || ex.customer_id || '\u2014'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{ex.state_code || '\u2014'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatDate(ex.issue_date)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatDate(ex.expiration_date)}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip label={getStatusLabel(ex.status)} size="small"
                                                sx={{
                                                    fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                    bgcolor: (STATUS_COLORS[ex.status] || STATUS_COLORS.PENDING).bg,
                                                    color: (STATUS_COLORS[ex.status] || STATUS_COLORS.PENDING).color,
                                                }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {ex.status === 'PENDING' && (
                                                    <Tooltip title="Verify">
                                                        <IconButton size="small" onClick={() => handleVerify(ex.id)}>
                                                            <VerifiedIcon sx={{ fontSize: 18, color: ACCENT }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Create Exemption Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Tax Exemption</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Certificate Number" required
                                value={formData.certificate_number}
                                onChange={e => setFormData(prev => ({ ...prev, certificate_number: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Exemption Type" required
                                value={formData.exemption_type}
                                onChange={e => setFormData(prev => ({ ...prev, exemption_type: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {EXEMPTION_TYPES.filter(t => t.value).map(t => (
                                    <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={customers}
                                getOptionLabel={opt => opt.name || opt.company_name || `Customer #${opt.id}`}
                                value={selectedCustomer}
                                onChange={(_, val) => { setSelectedCustomer(val); setFormData(prev => ({ ...prev, customer_id: val?.id || '' })); }}
                                onInputChange={(_, val) => setCustomerSearch(val)}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" label="Customer" placeholder="Search customers..."
                                        InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '10px' } }}
                                        InputLabelProps={{ sx: { fontFamily } }} />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="State Code" required
                                value={formData.state_code}
                                onChange={e => setFormData(prev => ({ ...prev, state_code: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {US_STATES.map(st => (
                                    <MenuItem key={st} value={st} sx={{ fontFamily }}>{st}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Issue Date" type="date" required
                                value={formData.issue_date}
                                onChange={e => setFormData(prev => ({ ...prev, issue_date: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Expiration Date" type="date"
                                value={formData.expiration_date}
                                onChange={e => setFormData(prev => ({ ...prev, expiration_date: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Document URL"
                                value={formData.document_url}
                                onChange={e => setFormData(prev => ({ ...prev, document_url: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={saving || !formData.certificate_number || !formData.state_code}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Check Exemption Dialog */}
            <Dialog open={checkOpen} onClose={() => { setCheckOpen(false); setCheckResult(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Check Customer Exemption</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Customer ID" required
                                value={checkCustomerId}
                                onChange={e => setCheckCustomerId(e.target.value)}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="State Code" required
                                value={checkStateCode}
                                onChange={e => setCheckStateCode(e.target.value)}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {US_STATES.map(st => (
                                    <MenuItem key={st} value={st} sx={{ fontFamily }}>{st}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                    {checkResult && (
                        <Card sx={{ mt: 2, p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, mb: 1 }}>Result</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569' }}>
                                Exempt: <strong>{checkResult.is_exempt ? 'Yes' : 'No'}</strong>
                            </Typography>
                            {checkResult.exemption && (
                                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569', mt: 0.5 }}>
                                    Certificate: {checkResult.exemption.certificate_number} | Type: {getStatusLabel(checkResult.exemption.exemption_type)} | Expires: {formatDate(checkResult.exemption.expiration_date)}
                                </Typography>
                            )}
                        </Card>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => { setCheckOpen(false); setCheckResult(null); }} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Close</Button>
                    <Button onClick={handleCheck} variant="contained" disabled={checking || !checkCustomerId || !checkStateCode}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {checking ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Check'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
